import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import millify from "millify";
import { cn } from "../../../utils/utils";
import ThumbIcon from "../../../assets/svgs/common/thumb.svg";
import { ThreeDWrapper } from "./3d-wrapper.core";

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    step?: number;
    max?: number;
    min?: number;
    stepsVisible?: boolean;
    overLabelVisible?: boolean;
    thumbWidth?: string;
    thumbHeight?: string;
    trackHeight?: string;
    decimalPlaces?: number;
    valueType?: "decimal" | "integer" | "percentage"; // Add valueType prop
  }
>(
  (
    {
      className,
      max = 20,
      min = 0,
      step = 0.001,
      stepsVisible,
      overLabelVisible,
      thumbHeight,
      thumbWidth,
      trackHeight,
      decimalPlaces = 3,
      valueType = "integer", // Default to integer
      ...props
    },
    ref
  ) => {
    const value = props.value ?? [min];

    const numDots = 20; // Number of dots to render for performance
    const dotStep = (max - min) / numDots;

    // Format value based on its magnitude and type
    const formatValue = (val: number) => {
      if (val === 0) return "0";

      if (valueType === "percentage") {
        return val.toFixed(decimalPlaces);
      }

      if (val < 1) {
        return val.toFixed(decimalPlaces);
      }
      if (val < 1000) {
        return val.toFixed(2);
      }
      return millify(val);
    };

    // Get prefix/suffix based on value type
    const getValueDisplay = (val: number) => {
      const formattedValue = formatValue(val);

      switch (valueType) {
        case "percentage":
          return `${formattedValue}%`;
        default:
          return `₹${formattedValue}`;
      }
    };

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex items-center w-full select-none touch-none",
          className
        )}
        {...props}
        value={value}
        max={max}
        min={min}
        step={step}
        onValueChange={props.onValueChange}
      >
        <SliderPrimitive.Track
          className={`relative flex justify-center items-center bg-slidertrack rounded-full w-full cursor-pointer overflow-hidden grow ${trackHeight ? trackHeight : "h-5"}`}
        >
          <SliderPrimitive.Range className="absolute bg-sliderrange h-full" />
          <div className="relative flex justify-evenly items-center w-full">
            {stepsVisible &&
              [...Array(numDots + 1)].map((_, index) => {
                const dot = min + index * dotStep;
                return (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    key={index}
                    className={`h-1 w-1 cursor-pointer rounded-full ${
                      dot <= (value.at(0) ?? min)
                        ? "bg-black"
                        : "bg-gray-800 bg-opacity-20"
                    }`}
                    style={{ left: `${((dot - min) / (max - min)) * 100}%` }}
                  />
                );
              })}
          </div>
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className="relative flex flex-col items-center disabled:opacity-50 cursor-pointer disabled:pointer-events-none focus-visible:outline-none">
          <ThumbIcon width={thumbWidth} height={thumbHeight} />
          {overLabelVisible && (
            <ThreeDWrapper
              hover={false}
              className="-top-12 absolute backdrop-blur p-0 rounded-md"
            >
              <div className="relative flex flex-col items-center px-2 py-1 rounded font-semibold">
                <span className="flex">
                  {Number.isNaN(value[0])
                    ? valueType === "percentage"
                      ? "0%"
                      : "₹0"
                    : getValueDisplay(value[0])}
                </span>
                <div className="-bottom-2 z-10 absolute bg-white border-black border-r-4 border-b w-3 h-3 rotate-45" />
              </div>
            </ThreeDWrapper>
          )}
        </SliderPrimitive.Thumb>
      </SliderPrimitive.Root>
    );
  }
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
